import {
    Box,
    Button,
    Checkbox,
    HStack,
    Icon,
    IconButton,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverFooter,
    PopoverTrigger,
    Text,
    Textarea,
    Tooltip,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState } from 'react';
import { TbThumbUp, TbThumbDown, TbThumbUpFilled, TbThumbDownFilled } from 'react-icons/tb';
import { AskCarlFeedbackDetails, AskCarlFeedbackRating, AskCarlMessage } from '../../types/askCarl';

/**
 * Thumbs up / thumbs down under a completed Carl response. Thumbs down opens
 * a short "what went wrong" form; submitting reports the whole thread (tool
 * call chain included) to the Carl feedback Slack channel for review.
 *
 * `showNudge` renders a "Was this helpful?" prompt — the page turns it on
 * for the latest response after a period of inactivity.
 */

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(4px); }
    to { opacity: 1; transform: translateY(0); }
`;

const THUMBS_DOWN_REASONS = [
    'Incorrect information',
    "Didn't do what I asked",
    'Wrong order or parts',
    'Took too long',
    'Confusing answer',
];

interface CarlFeedbackControlsProps {
    message: AskCarlMessage;
    onSubmit: (messageId: string, rating: AskCarlFeedbackRating, details?: AskCarlFeedbackDetails) => Promise<boolean>;
    showNudge?: boolean;
}

const CarlFeedbackControls = ({ message, onSubmit, showNudge }: CarlFeedbackControlsProps) => {
    const toast = useToast();
    const form = useDisclosure();
    const [reasons, setReasons] = useState<string[]>([]);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const rated = message.feedback;

    const handleThumbsUp = async () => {
        form.onClose();
        await onSubmit(message.id, 'up');
    };

    const handleThumbsDownSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmit(message.id, 'down', {
                reasons: reasons.length ? reasons : undefined,
                comment: comment.trim() || undefined,
            });
            toast({
                title: 'Thanks for the feedback',
                description: "We'll review this conversation to make Carl better.",
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            form.onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    const toggleReason = (reason: string) => {
        setReasons((prev) => (prev.includes(reason) ? prev.filter((entry) => entry !== reason) : [...prev, reason]));
    };

    if (rated) {
        return (
            <HStack spacing={1.5} mt={1} animation={`${fadeIn} 0.2s ease-out`}>
                <Icon
                    as={(rated === 'up' ? TbThumbUpFilled : TbThumbDownFilled) as React.ElementType}
                    boxSize={3.5}
                    color={rated === 'up' ? '#0071E3' : 'gray.500'}
                />
                <Text fontSize="xs" color="gray.500">Thanks for your feedback</Text>
            </HStack>
        );
    }

    return (
        <HStack spacing={1} mt={1} animation={`${fadeIn} 0.2s ease-out`}>
            {showNudge && (
                <Text fontSize="xs" color="gray.500" mr={1} animation={`${fadeIn} 0.3s ease-out`}>
                    Was this helpful?
                </Text>
            )}
            <Tooltip label="Good response" fontSize="xs" hasArrow>
                <IconButton
                    aria-label="Good response"
                    icon={<Icon as={TbThumbUp as React.ElementType} boxSize={3.5} />}
                    size="xs"
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: '#0071E3', bg: 'blue.50' }}
                    onClick={handleThumbsUp}
                />
            </Tooltip>
            <Popover
                isOpen={form.isOpen}
                onOpen={form.onOpen}
                onClose={form.onClose}
                placement="bottom-start"
                closeOnBlur={!isSubmitting}
            >
                <PopoverTrigger>
                    <Box display="inline-block">
                        <Tooltip label="Bad response" fontSize="xs" hasArrow isDisabled={form.isOpen}>
                            <IconButton
                                aria-label="Bad response"
                                icon={<Icon as={TbThumbDown as React.ElementType} boxSize={3.5} />}
                                size="xs"
                                variant="ghost"
                                color="gray.400"
                                _hover={{ color: 'red.500', bg: 'red.50' }}
                            />
                        </Tooltip>
                    </Box>
                </PopoverTrigger>
                <PopoverContent w="320px" boxShadow="none" borderColor="gray.200" _focus={{ boxShadow: 'none' }}>
                    <PopoverArrow />
                    <PopoverBody pt={3}>
                        <VStack align="stretch" spacing={2.5}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                What went wrong?
                            </Text>
                            <VStack align="stretch" spacing={1}>
                                {THUMBS_DOWN_REASONS.map((reason) => (
                                    <Checkbox
                                        key={reason}
                                        size="sm"
                                        colorScheme="blue"
                                        isChecked={reasons.includes(reason)}
                                        onChange={() => toggleReason(reason)}
                                    >
                                        <Text fontSize="sm" color="gray.700">{reason}</Text>
                                    </Checkbox>
                                ))}
                            </VStack>
                            <Textarea
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                placeholder="Tell us more (optional) — what did you expect Carl to do?"
                                size="sm"
                                borderRadius="md"
                                rows={3}
                                resize="vertical"
                            />
                        </VStack>
                    </PopoverBody>
                    <PopoverFooter display="flex" justifyContent="flex-end" gap={2} border="none" pt={0} pb={3}>
                        <Button size="xs" variant="outlineGray" onClick={form.onClose} isDisabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button
                            size="xs"
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#005BC4' }}
                            onClick={handleThumbsDownSubmit}
                            isLoading={isSubmitting}
                            isDisabled={reasons.length === 0 && !comment.trim()}
                        >
                            Send feedback
                        </Button>
                    </PopoverFooter>
                </PopoverContent>
            </Popover>
        </HStack>
    );
};

export default CarlFeedbackControls;
